import { Module } from 'vuex';

import { ShiftCMSState } from '@/store';
import { Story } from '@/models/story.model';
import storiesService from '@/services/stories.service';
import storyItemService from '@/services/story-items.service';
import { StoryItem } from '@/models/story-item.model';

export interface StoriesState {
  story: Story | null;
  storyItem: StoryItem | null;
  stories: Story[];
}

export enum StoriesMutations {
  SetStory = 'ts/SetStory',
  SetStories = 'ts/SetStories',
  SetStoryItem = 'ts/SetStoryItem',
}

export enum StoriesActions {
  GetStory = 'ts/GetStory',
  GetStories = 'ts/GetStories',
  AddStory = 'ts/AddStory',
  UpdateStory = 'ts/UpdateStory',
  AddStoryItem = 'ts/AddStoryItem',
  UpdateStoryItem = 'ts/UpdateStoryItem',
  RemoveStoryItem = 'ts/RemoveStoryItem',
  UpdateStoryItemStructure = 'ts/UpdateStoryItemStructure',
  UpdateStorySorting = 'ts/UpdateStorySorting',
  GetStoryItem = 'ts/GetStoryItem',
  DeleteStory = 'ts/DeleteStory',
}

const sn = (str: StoriesActions | StoriesMutations): string =>
  str.replace(/ts\//g, '');

export const storiesStore: Module<StoriesState, ShiftCMSState> = {
  namespaced: true,
  state: {
    story: null,
    storyItem: null,
    stories: [],
  },
  mutations: {
    [sn(StoriesMutations.SetStory)](state, story: Story | null) {
      state.story = story;
    },
    [sn(StoriesMutations.SetStories)](state, stories: Story[]) {
      state.stories = stories;
    },
    [sn(StoriesMutations.SetStoryItem)](state, storyItem: StoryItem | null) {
      state.storyItem = storyItem;
    },
  },
  actions: {
    async [sn(StoriesActions.GetStories)]({ commit }) {
      const res = await storiesService.getList();
      if (res.status) {
        commit(sn(StoriesMutations.SetStories), res.data);
      }
    },
    async [sn(StoriesActions.GetStory)]({ commit }, id) {
      const res = await storiesService.get(id);
      if (res.status) {
        commit(sn(StoriesMutations.SetStory), res.data);
        return res.data;
      }
    },
    async [sn(StoriesActions.AddStory)](_, story: Story) {
      const res = await storiesService.create({
        ...story,
        id: undefined,
      });

      return res.status;
    },
    async [sn(StoriesActions.UpdateStory)]({ commit }, story: Story) {
      const res = await storiesService.edit(story);

      if (res.status) {
        commit(sn(StoriesMutations.SetStory), res.data);
        return res.data;
      }

      return res.status;
    },
    async [sn(StoriesActions.GetStoryItem)]({ commit }, id) {
      const res = await storyItemService.get(id);
      if (res.status) {
        commit(sn(StoriesMutations.SetStoryItem), res.data);
        return res.data;
      }
    },
    async [sn(StoriesActions.AddStoryItem)](_, storyItem: StoryItem) {
      const res = await storyItemService.create({
        ...storyItem,
        id: undefined,
      });

      return res.status;
    },
    async [sn(StoriesActions.UpdateStoryItem)](_, storyItem: StoryItem) {
      const res = await storyItemService.edit(storyItem);

      return res.status;
    },
    async [sn(StoriesActions.RemoveStoryItem)](
      { dispatch },
      storyItem: StoryItem,
    ) {
      await storyItemService.delete(storyItem.story.id, storyItem.id);

      dispatch(sn(StoriesActions.GetStory), storyItem.story.id);
      return true;
    },
    async [sn(StoriesActions.UpdateStoryItemStructure)](
      { commit },
      storyItem: StoryItem,
    ) {
      const res = await storiesService.editStructure(storyItem);

      commit(sn(StoriesMutations.SetStory), res.data);
      return res.status;
    },
    async [sn(StoriesActions.UpdateStorySorting)]({ commit }, order: number[]) {
      const res = await storiesService.updateOrder(order);
      commit(sn(StoriesMutations.SetStory), res.data);
      return res.status;
    },
    async [sn(StoriesActions.DeleteStory)]({ dispatch }, id: number) {
      const res = await storiesService.delete(id);

      dispatch(sn(StoriesActions.GetStories));

      return res.status;
    },
  },
};
